/**
 * PassportAnalysisHistory — permanent record of all AI analyses the learner
 * has paid for. Shown on the Passport page so paid output is never lost.
 *
 * Directive (May 2 2026):
 *   • Paid AI output must land somewhere permanent.
 *   • The user must feel "my progress is saved, tracked, and building over time".
 *
 * The card is quiet when empty (returns null) — no noisy "no analyses yet"
 * placeholder — so first-time learners aren't nagged. As soon as the first
 * analysis completes it surfaces here.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Sparkles, ArrowRight, Calendar, Info } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

function formatDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export default function PassportAnalysisHistory() {
  const nav = useNavigate();
  const [entries, setEntries] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(
          `${API_URL}/api/passport/analysis-entries`,
          { withCredentials: true }
        );
        if (alive) setEntries(Array.isArray(data?.entries) ? data.entries : []);
      } catch {
        if (alive) setEntries([]);
      } finally {
        if (alive) setLoaded(true);
      }
    })();
    return () => { alive = false; };
  }, []);

  if (!loaded || entries.length === 0) return null;

  return (
    <section
      data-testid="passport-analysis-history"
      className="rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 overflow-hidden"
    >
      <header className="px-4 py-3 border-b border-slate-100 dark:border-slate-700 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-amber-100 dark:bg-amber-900/20 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-amber-600" />
          </div>
          <div>
            <h3 className="text-sm font-extrabold text-slate-900 dark:text-white">
              AI Lesson Analyses
            </h3>
            <p className="text-[11px] text-slate-500 dark:text-slate-400">
              Your saved driving feedback — always here when you need it.
            </p>
          </div>
        </div>
        <span className="text-[11px] font-bold text-slate-500 dark:text-slate-400">
          {entries.length} saved
        </span>
      </header>

      <ul className="divide-y divide-slate-100 dark:divide-slate-700">
        {entries.slice(0, 6).map((entry) => (
          <li
            key={entry.id}
            id={`row-clip-${entry.source_id || entry.id}`}
          >
            <button
              type="button"
              data-testid={`analysis-entry-${entry.id}`}
              onClick={() => entry.report_link && nav(entry.report_link)}
              className="w-full text-left px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-700/40 transition-colors"
            >
              <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-extrabold text-slate-900 dark:text-white">
                    {entry.title || "AI Lesson Analysis"}
                  </p>
                  {entry.track === "independent" && (
                    <span
                      data-testid={`analysis-entry-${entry.id}-independent-badge`}
                      className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase tracking-wide bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-200 border border-indigo-200 dark:border-indigo-700/40"
                      title="Independent learning — AI guidance, not instructor-confirmed"
                    >
                      <Info className="w-2.5 h-2.5" />
                      Not instructor-confirmed
                    </span>
                  )}
                  {entry.summary && (
                    <p className="text-[12px] text-slate-600 dark:text-slate-300 mt-0.5 line-clamp-2">
                      {entry.summary}
                    </p>
                  )}
                  {Array.isArray(entry.top_points) && entry.top_points.length > 0 && (
                    <ul className="mt-1.5 space-y-0.5">
                      {entry.top_points.slice(0, 2).map((point, i) => (
                        <li
                          key={i}
                          className="text-[11.5px] text-amber-700 dark:text-amber-400 flex items-start gap-1.5"
                        >
                          <span className="text-[14px] leading-none mt-[1px]">•</span>
                          <span className="flex-1">{point}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  <p className="text-[10px] text-slate-400 mt-1.5 flex items-center gap-1">
                    <Calendar className="w-2.5 h-2.5" />
                    {formatDate(entry.completed_at)}
                  </p>
                </div>
                <ArrowRight className="w-4 h-4 text-slate-300 flex-none mt-1" />
              </div>
            </button>
          </li>
        ))}
      </ul>

      {entries.length > 6 && (
        <footer className="px-4 py-2 text-center border-t border-slate-100 dark:border-slate-700">
          <span className="text-[11px] text-slate-400">
            {entries.length - 6} more saved
          </span>
        </footer>
      )}
    </section>
  );
}
